import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Divider,
} from '@mui/material'
import {
  AttachMoney as AttachMoneyIcon,
  Note as NoteIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { useUserActions } from '../../hooks/useUserActions'

const UserActionDialog = ({ open, type, user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    status: '',
    reason: '',
    amount: '',
    operation: 'add',
    note: '',
    category: 'general',
  })
  const [error, setError] = useState(null)

  const { updateUserStatus, updateUserCredits, addUserNote, isLoading } = useUserActions()

  useEffect(() => {
    if (open && user) {
      setFormData({
        status: user.status || '',
        reason: '',
        amount: '',
        operation: 'add',
        note: '',
        category: 'general',
      })
      setError(null)
    }
  }, [open, user])

  const handleClose = () => {
    setFormData({
      status: '',
      reason: '',
      amount: '',
      operation: 'add',
      note: '',
      category: 'general',
    })
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    try {
      setError(null)

      switch (type) {
        case 'suspend':
        case 'activate':
        case 'ban':
          const newStatus = type === 'suspend' ? 'suspended' : type === 'activate' ? 'active' : 'banned'
          await updateUserStatus(user.id, {
            status: newStatus,
            reason: formData.reason,
          })
          break

        case 'credits':
          if (!formData.amount || parseFloat(formData.amount) <= 0) {
            setError('Geçerli bir kredi miktarı giriniz')
            return
          }
          await updateUserCredits(user.id, {
            amount: parseFloat(formData.amount),
            operation: formData.operation,
            reason: formData.reason,
          })
          break

        case 'note':
          if (!formData.note.trim()) {
            setError('Not içeriği gereklidir')
            return
          }
          await addUserNote(user.id, {
            note: formData.note.trim(),
            category: formData.category,
          })
          break

        default:
          setError('Geçersiz işlem tipi')
          return
      }

      onSuccess()
    } catch (err) {
      setError(err.message || 'İşlem sırasında hata oluştu')
    }
  }

  const getDialogContent = () => {
    switch (type) {
      case 'suspend':
        return {
          title: 'Kullanıcıyı Askıya Al',
          icon: <BlockIcon color="warning" />,
          description: `${user?.email} adlı kullanıcıyı askıya almak istediğinizden emin misiniz?`,
          fields: (
            <TextField
              fullWidth
              label="Askıya alma nedeni"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Kullanıcıyı neden askıya aldığınızı açıklayın..."
              sx={{ mt: 2 }}
            />
          ),
        }

      case 'activate':
        return {
          title: 'Kullanıcıyı Aktifleştir',
          icon: <CheckCircleIcon color="success" />,
          description: `${user?.email} adlı kullanıcıyı aktifleştirmek istediğinizden emin misiniz?`,
          fields: (
            <TextField
              fullWidth
              label="Aktifleştirme nedeni"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Kullanıcıyı neden aktifleştirdiğinizi açıklayın..."
              sx={{ mt: 2 }}
            />
          ),
        }

      case 'ban':
        return {
          title: 'Kullanıcıyı Yasakla',
          icon: <PersonIcon color="error" />,
          description: `${user?.email} adlı kullanıcıyı kalıcı olarak yasaklamak istediğinizden emin misiniz?`,
          fields: (
            <TextField
              fullWidth
              label="Yasaklama nedeni (zorunlu)"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Kullanıcıyı neden yasakladığınızı detaylı olarak açıklayın..."
              required
              sx={{ mt: 2 }}
            />
          ),
        }

      case 'credits':
        return {
          title: 'Kredi Yönetimi',
          icon: <AttachMoneyIcon color="success" />,
          description: `${user?.email} adlı kullanıcının kredi bakiyesini yönetin`,
          fields: (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mevcut Bakiye
                </Typography>
                <Typography variant="h6" color="success.main">
                  {user?.credit_balance || 0} kredi
                </Typography>
              </Box>

              <RadioGroup
                value={formData.operation}
                onChange={(e) => setFormData(prev => ({ ...prev, operation: e.target.value }))}
                row
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="add" control={<Radio />} label="Kredi Ekle" />
                <FormControlLabel value="subtract" control={<Radio />} label="Kredi Çıkar" />
              </RadioGroup>

              <TextField
                fullWidth
                label="Kredi Miktarı"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 1, step: 1 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="İşlem nedeni"
                multiline
                rows={2}
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Kredi değişikliğinin nedenini açıklayın..."
              />
            </Box>
          ),
        }

      case 'note':
        return {
          title: 'Kullanıcı Notu Ekle',
          icon: <NoteIcon color="info" />,
          description: `${user?.email} adlı kullanıcı için not ekleyin`,
          fields: (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Not Kategorisi</InputLabel>
                <Select
                  value={formData.category}
                  label="Not Kategorisi"
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="general">Genel</MenuItem>
                  <MenuItem value="support">Destek</MenuItem>
                  <MenuItem value="billing">Ödeme</MenuItem>
                  <MenuItem value="security">Güvenlik</MenuItem>
                  <MenuItem value="warning">Uyarı</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Not İçeriği"
                multiline
                rows={4}
                value={formData.note}
                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Kullanıcı hakkında not ekleyin..."
                required
              />
            </Box>
          ),
        }

      default:
        return {
          title: 'Bilinmeyen İşlem',
          icon: null,
          description: 'Geçersiz işlem tipi',
          fields: null,
        }
    }
  }

  if (!user || !type) return null

  const content = getDialogContent()

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {content.icon}
          <Typography variant="h6">{content.title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {content.description}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {content.fields}

        {(type === 'ban' || type === 'suspend') && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: 1, borderColor: 'warning.200' }}>
            <Typography variant="body2" color="warning.800">
              <strong>Uyarı:</strong> Bu işlem kullanıcının hesap durumunu değiştirecektir ve admin panelinde kayıt altına alınacaktır.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          color={type === 'ban' ? 'error' : type === 'suspend' ? 'warning' : 'primary'}
        >
          {isLoading ? 'İşleniyor...' : 'Onayla'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserActionDialog